import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import DefaultResponse from '../models/responses/Default.Response.Model';
import { UserModel } from '../models/common/User.Model';

// Interfaz para los datos del menú que vienen de la API
export interface MenuOption {
    id: number;
    system_key: string;
    module_key: string;
    module_name: string;
    option_key: string;
    option_name: string;
    route: string;
    icon: string;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private currentUserSubject: BehaviorSubject<UserModel | null>;
    public currentUser: Observable<UserModel | null>;

    // BehaviorSubject para manejar los items del menú dinámico
    private menuItemsSubject: BehaviorSubject<MenuOption[] | null>;
    public menuItems: Observable<MenuOption[] | null>;

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        // Se mantiene tu lógica original para cargar el usuario
        const userData = localStorage.getItem('currentUser');
        const initialUser = userData ? JSON.parse(userData) : null;
        this.currentUserSubject = new BehaviorSubject<UserModel | null>(initialUser);
        this.currentUser = this.currentUserSubject.asObservable();

        // Se añade la lógica para cargar el menú desde localStorage al iniciar
        const menuData = localStorage.getItem('userMenu');
        const initialMenu = menuData ? JSON.parse(menuData) : null;
        this.menuItemsSubject = new BehaviorSubject<MenuOption[] | null>(initialMenu);
        this.menuItems = this.menuItemsSubject.asObservable();
    }

    public get currentUserValue(): UserModel | null {
        return this.currentUserSubject.value;
    }

    login(username: string, password: string) {
        return this.http.post<DefaultResponse<UserModel>>(`${environment.sUrl}auth/login`, { username, password }).pipe(
            tap((response) => {
                if (response.status && response.accessToken) {
                    const token = response.accessToken;
                    localStorage.setItem('token', token);

                    const userData = response.data ? (Array.isArray(response.data) ? response.data[0] : response.data) : null;
                    localStorage.setItem('currentUser', JSON.stringify(userData));
                    this.currentUserSubject.next(userData);

                    // Después del login, cargamos el menú del usuario
                    this.loadUserMenu(token).subscribe({
                        next: () => {
                            const returnUrl = localStorage.getItem('returnUrl') || '/';
                            localStorage.removeItem('returnUrl');
                            this.router.navigate([returnUrl]);
                        },
                        error: (err) => {
                            console.error("Fallo al cargar el menú de usuario", err);
                            this.router.navigate(['/']); // Navegar incluso si el menú falla
                        }
                    });
                }
            })
        );
    }

    // Nueva función para obtener y guardar el menú
    loadUserMenu(token: string): Observable<MenuOption[]> {
        const headers = new HttpHeaders({ 'Authorization': `Bearer ${token}` });
        const body = { system_key: 'SDC' };

        return this.http.post<MenuOption[]>(`${environment.sUrl}module-access`, body, { headers }).pipe(
            tap(menu => {
                localStorage.setItem('userMenu', JSON.stringify(menu));
                this.menuItemsSubject.next(menu);
            })
        );
    }

    getMyAccount(): Observable<DefaultResponse<UserModel>> {
        return this.http.get<DefaultResponse<UserModel>>(`${environment.sUrl}auth/my-account`);
    }

    logout() {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('token');
    localStorage.removeItem('returnUrl');
    localStorage.removeItem('userMenu');

    this.currentUserSubject.next(null);
    this.menuItemsSubject.next(null);

    // con withHashLocation NO pegues al servidor
    location.hash = '#/login';              // cambio inmediato de hash
    setTimeout(() => location.replace('#/login'), 50); // asegura replace en el historial
    }
}
